import * as React from 'react';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useLocation } from 'react-router-dom';

import { GetEventDetailedQuery } from '@graphql';
import { ParticipantsManage } from './ParticipantsManage';
import { truncateText } from '@utils';
import { DialogEditContext, Wrapper, WrapperContext } from './layout';

interface ParticipantsProps {
  eventId: string;
  creatorId: string;
  participants?: GetEventDetailedQuery['event']['participants']['list'];
}

export function Participants(props: ParticipantsProps) {
  const context = React.useContext(DialogEditContext);

  if (!context) {
    throw new Error('Participants must be used inside DialogEditWrapper');
  }

  const { isEditMode } = context;
  const { participants = [] } = props;
  const { t } = useTranslation('event');

  if (!isEditMode && participants.length === 0) {
    return null;
  }

  return (
    <Wrapper type="participant">
      <Wrapper.Header
        title={t('title.Participants')}
        openCtaLabel={participants.length ? 'manage' : 'add'}
        forceDisplayCta={true}
      />
      <Wrapper.Body>
        <ParticipantsInner {...props} />
      </Wrapper.Body>
    </Wrapper>
  );
}

interface ParticipantsInnerProps extends ParticipantsProps {}

function ParticipantsInner(props: ParticipantsInnerProps) {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('ParticipantsInner must be used inside Wrapper');
  }

  const { isOwner, managingType, isManaging } = context;
  const { participants = [], eventId, creatorId } = props;
  const theme = useTheme();
  const [_searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { t } = useTranslation('event');

  if (!isOwner && participants.length === 0) {
    return null;
  }

  const PARTICIPANT_TO_SHOW = 5;
  const AND_MORE = participants.length - PARTICIPANT_TO_SHOW;

  const handleClickConsumer = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const slug = (e.currentTarget as HTMLElement).dataset.slug;
    const params = new URLSearchParams(location.search);
    params.set('c', slug ?? '');
    setSearchParams(params, { state: { from: location.pathname + (location.search ?? '') } });
  };

  return (
    <Stack spacing={2}>
      {isManaging === managingType ? (
        <ParticipantsManage eventId={eventId} creatorId={creatorId} participants={participants} />
      ) : (
        <Stack id="participants-list" spacing={2} divider={<Divider />}>
          {!participants.length ? (
            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
              {t('text.No participant yet')}
            </Typography>
          ) : (
            participants
              .filter((_p, idx) => idx < PARTICIPANT_TO_SHOW)
              .map((participant) => (
                <Stack
                  key={participant.id}
                  component={Link}
                  to={location.pathname}
                  data-slug={participant.account.slug}
                  state={{ from: location.pathname + (location.search ?? '') }}
                  onClick={handleClickConsumer}
                  direction="row"
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AvatarWithSkeleton
                      src={participant.account.avatar?.url}
                      alt={participant.account.name}
                      sx={{ width: { xs: 30, sm: 40 }, height: { xs: 30, sm: 40 } }}
                    />
                    <Stack alignItems="flex-start" justifyContent="center">
                      <Typography fontWeight="bold" fontSize={{ xs: '.8rem', sm: '.9rem' }}>
                        {truncateText(participant.account.name, 25)}
                      </Typography>
                      <Typography color="textSecondary" fontSize={{ xs: '.7rem', sm: '.8rem' }}>
                        @{truncateText(participant.account.slug, 25)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      size="small"
                      variant="contained"
                      label={t(`participant.status.${participant.status}`)}
                      sx={{
                        height: 28,
                        fontSize: '0.65rem',
                        px: 0.5,
                        '& .MuiChip-label': {
                          px: 0.5,
                        },
                        '& .MuiChip-icon': {
                          fontSize: '1rem',
                          ml: 0.5,
                        },
                      }}
                    />
                    <theme.icons.chevron
                      sx={{ color: 'text.secondary', width: { xs: 20, sm: 30 }, height: { xs: 20, sm: 30 } }}
                    />
                  </Stack>
                </Stack>
              ))
          )}
          {AND_MORE > 0 && (
            <Typography color="textSecondary" fontSize={{ xs: '.7rem', sm: '.8rem' }}>
              {t('text.And x more', { count: AND_MORE })}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

interface AvatarWithSkeletonProps {
  src?: string | null;
}

function AvatarWithSkeleton(props: Readonly<AvatarWithSkeletonProps & AvatarProps>) {
  const [loaded, setLoaded] = React.useState(false);
  const { src, alt, sx, ...otherProps } = props;

  return (
    <Box position="relative" sx={sx}>
      {!loaded && <Avatar sx={sx} />}

      <Avatar
        src={src ?? undefined}
        alt={alt}
        imgProps={{
          onLoad: () => setLoaded(true),
          onError: () => setLoaded(true),
        }}
        sx={{
          ...sx,
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: loaded ? 1 : 0,
        }}
      />
    </Box>
  );
}
